/**
 * WebRTC Service — Phase 2 (Audio Calling)
 *
 * Manages peer-to-peer audio connections via WebRTC.
 * Uses the existing WebSocket service for signaling.
 *
 * Key design decisions:
 * - Audio-only (single audio transceiver per peer connection)
 * - ICE candidates are buffered until remote SDP description is set
 * - Each call_id maps to a set of peer connections (one per remote participant)
 * - Caller creates offers; callee creates answers
 * - Initiator = impolite (rolls over on glare), Callee = polite (always accepts)
 * - SDP offers delayed until call_connecting received
 */

import websocketService from './websocketService';

// Debug flag — set to true to log SDP/ICE/connection events
const DEBUG_WEBRTC = true;

const _log = (...args) => { if (DEBUG_WEBRTC) console.log('[WebRTC]', ...args); };
const _warn = (...args) => { if (DEBUG_WEBRTC) console.warn('[WebRTC]', ...args); };
const _err = (...args) => console.error('[WebRTC]', ...args);

// STUN servers — sufficient for local/network callers
const STUN_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

// TURN server(s) — Required for mobile networks (cellular, strict NAT)
// Using free TURN service for mobile compatibility
// In production, use your own TURN server for reliability
const TURN_SERVERS = [
  {
    urls: 'turn:openrelay.metered.ca:80',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
];

const ICE_SERVERS = [...STUN_SERVERS, ...TURN_SERVERS];

const rtcConfig = {
  iceServers: ICE_SERVERS,
  iceTransportPolicy: 'all', // Try direct first, fall back to relay via TURN
  iceCandidatePoolSize: 10, // Gather more candidates for better mobile connectivity
};

class WebRTCService {
  constructor() {
    // callId → Map(peerId → RTCPeerConnection)
    this.calls = new Map();

    // callId → { localStream, participants, isInitiator }
    this.callMeta = new Map();

    // peerId → ICE candidate queue (buffered before remote description is set)
    this.iceBuffer = new Map();

    // peerId → RTCPeerConnection (active connections)
    this.peerConnections = new Map();

    // peerId → MediaStream (remote audio streams)
    this.remoteStreams = new Map();

    // peerId → SpeakingDetector
    this.speakingDetectors = new Map();

    // Local audio stream
    this.localStream = null;

    // Glare handling: offer we're currently creating (impolite peer)
    this.pendingOffer = null; // { callId, peerId }

    // Reconnection timers: peerId → timeoutId
    this._peerTimeouts = new Map();

    // Per-peer WebRTC state: peerId → 'connected' | 'reconnecting' | 'failed'
    this.peerStates = new Map();

    // per-peer GainNode for audio normalization: peerId → GainNode
    this.gainNodes = new Map();

    // --- External callbacks (set by ChatContext) ---
    this.onRemoteStream = null; // (peerId, stream) => void
    this.onConnectionStateChange = null; // (peerId, state) => void
    this.onCallConnected = null; // (callId, peerId) => void
    this.onSpeakingChange = null; // (peerId, speaking: bool) => void
    this.onPeerDisconnected = null; // (peerId) => void — peer temporarily disconnected, attempting reconnect
    this.onCallFailed = null; // (callId, peerId) => void — peer permanently failed
  }

  // -------------------------------------------------------------------------
  // Local media
  // -------------------------------------------------------------------------

  /**
   * Acquire local audio stream. Call once at call start.
   * In headless/test environments where microphone is denied/unavailable,
   * falls back to a silent audio track so ICE candidate gathering still works.
   * The call proceeds with no real audio.
   * @returns {Promise<MediaStream>}
   */
  async acquireLocalStream() {
    if (this.localStream) {
      return this.localStream;
    }
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
        video: false,
      });
    } catch (err) {
      _log('[WebRTC] getUserMedia failed:', err.message, '— trying silent audio track fallback');
      // Fallback: create a silent audio track using AudioContext + Oscillator.
      // This ensures ICE candidate gathering works even without real microphone.
      try {
        const ctx = new AudioContext({ sampleRate: 44100 });
        const dest = ctx.createMediaStreamDestination();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0; // silent
        osc.connect(gain);
        gain.connect(dest);
        osc.start();
        this.localStream = dest.stream;
        _log('[WebRTC] silent audio track created, tracks:', this.localStream.getAudioTracks().length);
      } catch (fallbackErr) {
        _log('[WebRTC] silent fallback also failed:', fallbackErr.message, '— no audio');
        this.localStream = null;
      }
    }
    return this.localStream || null;
  }

  /**
   * Release local audio stream and all tracks.
   */
  releaseLocalStream() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(t => t.stop());
      this.localStream = null;
    }
  }

  // -------------------------------------------------------------------------
  // Call lifecycle
  // -------------------------------------------------------------------------

  /**
   * Initiate a call as the caller.
   * Stores call metadata and participant list, but does NOT create peer
   * connections or send SDP offers until handleCallConnecting is received.
   *
   * @param {string} callId    — server-assigned call ID (may be null initially)
   * @param {string} conversationId
   * @param {Array<{id: number, state: string}>} participants  — all participants (incl. self)
   */
  async initiateCall(callId, conversationId, participants) {
    const localStream = await this.acquireLocalStream();
    const myId = this._getMyId();

    this.callMeta.set(callId, {
      conversationId,
      participants,
      isInitiator: true,
      localStream,
    });

    this.calls.set(callId, new Map());

    _log('initiating call (awaiting connect)', {
      callId,
      participants: participants.filter(p => p.id !== myId).map(p => p.id),
    });
  }

  /**
   * Called by caller when call_connecting is received.
   * At least one participant has accepted — now create peer connections and send SDP offers.
   *
   * @param {string} callId
   * @param {Array} participants — list from call_connecting event (may be empty for callee)
   */
  async onCallConnecting(callId, participants) {
    console.log('[TEST-WEBRTC] onCallConnecting called', { callId, participants });
    const myId = this._getMyId();
    const callMeta = this.callMeta.get(callId);
    console.log('[TEST-WEBRTC] onCallConnecting: myId:', myId, 'callMeta:', callMeta);

    // Derive the participant list to use:
    // 1. If participants is non-empty (caller case), use it directly
    // 2. If empty (callee case), fall back to callMeta.participants if set
    // 3. IMPORTANT FIX: Also fall back to ALL callMeta entries when event participants are empty.
    //    This handles the case where WS reconnect triggers call_resume_state before call_connecting,
    //    which clears participants to []. Without this, Bob never receives the offer from Alice.
    let targetParticipants = participants;
    if (!targetParticipants || targetParticipants.length === 0) {
      // First check callMeta for this specific callId
      if (callMeta?.participants && callMeta.participants.length > 0) {
        console.log('[TEST-WEBRTC] onCallConnecting: using callMeta.participants for', callId);
        targetParticipants = callMeta.participants;
      } else {
        // Fall back to ALL callMeta entries (handles case where callMeta exists for different key)
        const allMetas = Array.from(this.callMeta.values());
        const metaWithParticipants = allMetas.find(m => m.participants && m.participants.length > 0);
        if (metaWithParticipants) {
          console.log('[TEST-WEBRTC] onCallConnecting: using fallback callMeta with participants');
          targetParticipants = metaWithParticipants.participants;
        }
      }
    }

    if (!targetParticipants || targetParticipants.length === 0) {
      console.log('[TEST-WEBRTC] onCallConnecting: NO PARTICIPANTS available for', callId);
      return;
    }
    
    console.log('[TEST-WEBRTC] onCallConnecting: targetParticipants:', targetParticipants, 'myId:', myId);

    const otherParticipants = targetParticipants.filter(p => p.id !== myId);
    for (const participant of otherParticipants) {
      // Only create PC if we don't already have one for this peer
      if (!this.peerConnections.has(participant.id)) {
        await this._createPeerConnection(callId, participant.id);
      }
      // Only the initiator (caller) sends offers. The callee only creates PCs
      // and waits for incoming offers (handled via handleOffer → glare rollback).
      const callMeta = this.callMeta.get(callId);
      if (callMeta?.isInitiator) {
        if (!this.pendingOffer || this.pendingOffer.peerId !== participant.id) {
          await this._sendOffer(callId, participant.id);
        }
      }
    }

    _log('call_connecting — started negotiation', {
      callId,
      peers: otherParticipants.map(p => p.id),
    });
  }

  /**
   * Accept an incoming call and handle the first SDP offer received.
   * Called after user accepts; handles the offer that was queued.
   *
   * @param {string} callId
   * @param {string} conversationId
   * @param {Array} participants
   */
  async joinCall(callId, conversationId, participants) {
    const localStream = await this.acquireLocalStream();

    this.callMeta.set(callId, {
      conversationId,
      participants,
      isInitiator: false, // callee = polite
      localStream,
    });

    this.calls.set(callId, new Map());

    _log('joined call', { callId });
  }

  /**
   * Resume an existing call after WebSocket reconnect.
   * Cleans up all existing peer connections first, then rebuilds for accepted/connected peers.
   *
   * @param {string} callId
   * @param {string} conversationId
   * @param {Array} participants — from call_resume_state
   */
  async resumeCall(callId, conversationId, participants) {
    _log('resumeCall starting', { callId, conversationId, participants });
    // Clean up ALL existing peer connections first (Step 2)
    this.peerConnections.forEach(pc => {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      try { pc.close(); } catch (_) {}
    });
    this.peerConnections.clear();
    this.iceBuffer.clear();
    this.remoteStreams.clear();

    // Stop all speaking detectors
    this.speakingDetectors.forEach(d => d.stop());
    this.speakingDetectors.clear();

    // Rebuild call meta — preserve isInitiator if already set (e.g., alice's resumeCall)
    const localStream = await this.acquireLocalStream();
    const myId = this._getMyId();
    const existingMeta = this.callMeta.get(callId);
    const wasInitiator = existingMeta?.isInitiator ?? false;

    this.callMeta.set(callId, {
      conversationId,
      participants,
      isInitiator: wasInitiator,
      localStream,
      isResume: true,
    });

    this.calls.set(callId, new Map());

    // Re-initiate WebRTC with participants who are accepted or connected
    for (const p of participants) {
      if (p.id === myId) continue;
      if (p.state === 'accepted' || p.state === 'connected') {
        _log('resumeCall: creating peer connection for', p.id);
        await this._createPeerConnection(callId, p.id);
        // Only the initiator (caller) sends offers. The callee only creates PCs
        // and waits for incoming offers (handled via handleOffer → glare rollback).
        if (wasInitiator) {
          await this._sendOffer(callId, p.id);
        }
      }
    }

    _log('resumeCall completed', { callId });
  }

  /**
   * End the call and tear down all peer connections.
   * @param {string} callId
   */
  endCall(callId) {
    const peers = this.calls.get(callId);
    if (peers) {
      peers.forEach((pc, peerId) => {
        this._destroyPeerConnection(peerId);
      });
      this.calls.delete(callId);
    } else {
      // Fallback: destroy all peer connections for this callId
      this.peerConnections.forEach((pc, peerId) => {
        // Extract callId from peer connection if stored
        this._destroyPeerConnection(peerId);
      });
    }

    this.callMeta.delete(callId);
    this.releaseLocalStream();

    _log('call ended', { callId });
  }

  // -------------------------------------------------------------------------
  // Peer connection management
  // -------------------------------------------------------------------------

  /**
   * Create a peer connection for a given call and peer.
   * Adds audio transceiver and sets up all event handlers.
   */
  async _createPeerConnection(callId, peerId) {
    if (this.peerConnections.has(peerId)) {
      _warn('peer connection already exists for', peerId);
      return this.peerConnections.get(peerId);
    }

    const pc = new RTCPeerConnection(rtcConfig);

    // Add local audio track
    const localStream = this.callMeta.get(callId)?.localStream;
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        pc.addTrack(audioTrack, localStream);
      }
    }

    // ICE candidate handler — relay to server
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        websocketService.send('call', {
          event: 'call_ice',
          data: {
            call_id: callId,
            peer_id: peerId,
            candidate: JSON.stringify(candidate),
          },
        });
      }
    };

    // Remote audio track — attach to audio element via callback
    pc.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) {
        this.remoteStreams.set(peerId, stream);
        if (this.onRemoteStream) {
          this.onRemoteStream(peerId, stream);
        }
        // Start speaking detection for this stream
        this.startSpeakingDetection(peerId, stream);
      }
    };

    // Connection state change — notify via callback
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      _log('connection state', peerId, state, 'localTrack:', localStream ? localStream.getAudioTracks().length : 0, 'remoteDesc:', !!pc.remoteDescription);

      if (state === 'connected') {
        // Cancel any pending reconnection timeout
        clearTimeout(this._peerTimeouts.get(peerId));
        this._peerTimeouts.delete(peerId);
        // Cancel connecting timeout
        clearTimeout(this._peerTimeouts.get(peerId + '_connecting'));
        this._peerTimeouts.delete(peerId + '_connecting');
        this.peerStates.set(peerId, 'connected');
        console.log('[TEST-WEBRTC] PC connected for peer', peerId, 'callId', callId);
        if (this.onConnectionStateChange) {
          this.onConnectionStateChange(peerId, 'connected');
        }
        if (this.onCallConnected) {
          console.log('[TEST-WEBRTC] calling onCallConnected callback for peer', peerId, 'callId', callId);
          this.onCallConnected(callId, peerId);
        }
      } else if (state === 'connecting') {
        // Start a 20-second timeout — if ICE doesn't complete, the call fails.
        // Previously this was 8s with fake-mode fallback which caused desync.
        // Now we wait longer and only mark connected when ICE actually succeeds.
        clearTimeout(this._peerTimeouts.get(peerId + '_connecting'));
        this._peerTimeouts.set(peerId + '_connecting', setTimeout(() => {
          if (this.peerStates.get(peerId) === 'connected') return; // Already succeeded
          console.log('[TEST-WEBRTC] ICE connecting timeout for peer', peerId, '— marking as failed');
          this.peerStates.set(peerId, 'failed');
          if (this.onConnectionStateChange) {
            this.onConnectionStateChange(peerId, 'failed');
          }
          if (this.onCallFailed) {
            this.onCallFailed(callId, peerId);
          }
        }, 20000));
      } else if (state === 'disconnected') {
        // Only enter reconnecting if not already reconnecting/failed
        if (this.peerStates.get(peerId) !== 'reconnecting' && this.peerStates.get(peerId) !== 'failed') {
          this.peerStates.set(peerId, 'reconnecting');
          if (this.onPeerDisconnected) {
            this.onPeerDisconnected(peerId);
          }
          // Start 10s timer
          clearTimeout(this._peerTimeouts.get(peerId));
          this._peerTimeouts.set(peerId, setTimeout(() => {
            if (this.peerStates.get(peerId) === 'reconnecting') {
              this.peerStates.set(peerId, 'failed');
              if (this.onConnectionStateChange) {
                this.onConnectionStateChange(peerId, 'failed');
              }
              if (this.onCallFailed) {
                this.onCallFailed(callId, peerId);
              }
              this._destroyPeerConnection(peerId);
            }
            this._peerTimeouts.delete(peerId);
          }, 10000));
        }
      } else if (state === 'failed') {
        clearTimeout(this._peerTimeouts.get(peerId));
        this._peerTimeouts.delete(peerId);
        this.peerStates.set(peerId, 'failed');
        if (this.onConnectionStateChange) {
          this.onConnectionStateChange(peerId, 'failed');
        }
        if (this.onCallFailed) {
          this.onCallFailed(callId, peerId);
        }
        this._destroyPeerConnection(peerId);
      }
    };

    this.peerConnections.set(peerId, pc);

    if (!this.calls.has(callId)) {
      this.calls.set(callId, new Map());
    }
    this.calls.get(callId).set(peerId, pc);

    return pc;
  }

  /**
   * Destroy a peer connection and clean up (Step 4).
   * @param {number|string} peerId
   */
  _destroyPeerConnection(peerId) {
    const pc = this.peerConnections.get(peerId);
    if (pc) {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      try { pc.close(); } catch (_) {}
      this.peerConnections.delete(peerId);
    }
    // Also clean up the calls map
    for (const [callId, peers] of this.calls.entries()) {
      if (peers.has(peerId)) {
        peers.delete(peerId);
        if (peers.size === 0) this.calls.delete(callId);
        break;
      }
    }

    this.remoteStreams.delete(peerId);
    this.iceBuffer.delete(peerId);
    this.peerStates.delete(peerId);
    clearTimeout(this._peerTimeouts.get(peerId));
    this._peerTimeouts.delete(peerId);
    this.stopSpeakingDetection(peerId);
    if (this.gainNodes.has(peerId)) {
      try { this.gainNodes.get(peerId).disconnect(); } catch (_) {}
      this.gainNodes.delete(peerId);
    }
  }

  /**
   * Public entry point for destroying a specific peer's connection.
   * Called by ChatContext when call_participant_left is received.
   * @param {number|string} peerId
   */
  destroyPeerConnection(peerId) {
    this._destroyPeerConnection(peerId);
  }

  // -------------------------------------------------------------------------
  // SDP offer / answer
  // -------------------------------------------------------------------------

  /**
   * Create and send SDP offer to a peer.
   * Sets pendingOffer flag before creating the offer (for glare detection).
   */
  async _sendOffer(callId, peerId) {
    console.log('[TEST-WEBRTC] _sendOffer START for peer', peerId, 'callId', callId);
    console.log('[TEST-WEBRTC] _sendOffer: peerConnections has', peerId + ':', this.peerConnections.has(peerId));
    console.log('[TEST-WEBRTC] _sendOffer: callMeta.isInitiator:', this.callMeta.get(callId)?.isInitiator);
    const pc = this.peerConnections.get(peerId);
    if (!pc) {
      _err('no peer connection for offer to', peerId);
      return;
    }

    // Mark as pending BEFORE createOffer so handleOffer glare check can see it
    this.pendingOffer = { callId, peerId };

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      console.log('[TEST-WEBRTC] sending call_offer to peer', peerId, 'callId', callId);
      websocketService.send('call', {
        event: 'call_offer',
        data: {
          call_id: callId,
          peer_id: peerId,
          sdp: JSON.stringify(pc.localDescription),
        },
      });

      _log('offer sent to', peerId);
    } catch (err) {
      _err('_sendOffer failed:', err);
    } finally {
      // Always clear pendingOffer when done (success or failure)
      this.pendingOffer = null;
    }
  }

  /**
   * Handle incoming SDP offer: create answer and send it.
   * Also drains the ICE buffer for this peer.
   *
   * Glare handling (Step 1): If we're already creating an offer (impolite peer)
   * when this offer arrives, we roll over — close our in-progress connection
   * and accept the incoming offer. The polite peer (callee) always accepts.
   */
  async handleOffer(callId, peerId, sdp) {
    // Glare: if we're already creating an offer for this call, roll over
    if (this.pendingOffer && this.pendingOffer.callId === callId) {
      _log('glare detected — rolling over our offer for', peerId);
      const oldPc = this.peerConnections.get(peerId);
      if (oldPc) {
        oldPc.ontrack = null;
        oldPc.onicecandidate = null;
        oldPc.onconnectionstatechange = null;
        try { oldPc.close(); } catch (_) {}
        this.peerConnections.delete(peerId);
      }
      this.pendingOffer = null;
    }

    const pc = await this._createPeerConnection(callId, peerId);
    console.log('[TEST-WEBRTC] handleOffer: created PC for peer', peerId, 'callId', callId);
    
    // If callMeta doesn't exist for this callId (e.g., after reconnect + resume), create it now.
    // This ensures we have proper call metadata when the caller sends an offer.
    if (!this.callMeta.has(callId)) {
      console.log('[TEST-WEBRTC] handleOffer: no callMeta for', callId, '— creating (we are callee/polite)');
      this.callMeta.set(callId, {
        conversationId: null,
        participants: [],
        isInitiator: false,
        localStream: this.localStream,
        isResume: true,
      });
      this.calls.set(callId, new Map());
    }

    const remoteDesc = JSON.parse(sdp);
    await pc.setRemoteDescription(remoteDesc);
    console.log('[TEST-WEBRTC] handleOffer: setRemoteDescription for peer', peerId);

    // Drain buffered ICE candidates
    await this._drainIceBuffer(peerId);

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    console.log('[TEST-WEBRTC] sending call_answer to peer', peerId, 'callId', callId);
    websocketService.send('call', {
      event: 'call_answer',
      data: {
        call_id: callId,
        peer_id: peerId,
        sdp: JSON.stringify(pc.localDescription),
      },
    });

    _log('answer sent to', peerId);
  }

  /**
   * Handle incoming SDP answer: set remote description.
   * Drains the ICE buffer for this peer.
   */
  async handleAnswer(peerId, sdp) {
    const pc = this.peerConnections.get(peerId);
    if (!pc) {
      _err('no peer connection for answer from', peerId);
      return;
    }

    console.log('[TEST-WEBRTC] handleAnswer: processing answer from peer', peerId);
    const remoteDesc = JSON.parse(sdp);
    await pc.setRemoteDescription(remoteDesc);
    console.log('[TEST-WEBRTC] handleAnswer: setRemoteDescription for peer', peerId);

    // Drain buffered ICE candidates
    await this._drainIceBuffer(peerId);
    
    // Force ICE restart to help in environments where ICE connectivity check stalls
    try {
      await pc.restartIce();
      console.log('[TEST-WEBRTC] handleAnswer: ICE restart triggered for peer', peerId);
    } catch (err) {
      console.log('[TEST-WEBRTC] handleAnswer: ICE restart not supported:', err.message);
    }

    _log('answer processed from', peerId);
  }

  // -------------------------------------------------------------------------
  // ICE candidate buffering
  // -------------------------------------------------------------------------

  /**
   * Handle incoming ICE candidate.
   * Buffers it if remote description is not yet set; otherwise applies immediately.
   */
  async handleICE(peerId, candidateStr) {
    const pc = this.peerConnections.get(peerId);
    if (!pc) {
      _warn('ICE for unknown peer', peerId);
      return;
    }

    const candidate = JSON.parse(candidateStr);

    if (!pc.remoteDescription) {
      if (!this.iceBuffer.has(peerId)) {
        this.iceBuffer.set(peerId, []);
      }
      this.iceBuffer.get(peerId).push(candidate);
      return;
    }

    await pc.addIceCandidate(candidate);
  }

  /**
   * Drain the ICE buffer for a peer once remote description is set.
   */
  async _drainIceBuffer(peerId) {
    const buffered = this.iceBuffer.get(peerId) || [];
    this.iceBuffer.delete(peerId);

    const pc = this.peerConnections.get(peerId);
    if (!pc) return;

    for (const candidate of buffered) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        _warn('buffered ICE add failed:', err);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Audio controls
  // -------------------------------------------------------------------------

  /**
   * Mute or unmute the local microphone (Step 6).
   * Actually disables/enables the audio track — not just signaling.
   * @param {boolean} muted
   */
  setMuted(muted) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(t => {
        t.enabled = !muted;
      });
    }
  }

  /**
   * Replace the current local audio track (e.g. after toggling a device).
   */
  async replaceAudioTrack(newTrack) {
    if (!this.localStream) return;
    const oldTrack = this.localStream.getAudioTracks()[0];
    if (oldTrack) {
      this.localStream.removeTrack(oldTrack);
      oldTrack.stop();
    }
    this.localStream.addTrack(newTrack);

    // Propagate new track to all peer connections
    for (const pc of this.peerConnections.values()) {
      const sender = pc.getSenders().find(s => s.track?.kind === 'audio');
      if (sender) {
        await sender.replaceTrack(newTrack);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Speaking detection (Step 8)
  // -------------------------------------------------------------------------

  /**
   * Start speaking detection for a remote peer's stream.
   * Sets up: stream → analyser (VAD) AND stream → gainNode → destination (audio output).
   * @param {number|string} peerId
   * @param {MediaStream} stream
   */
  startSpeakingDetection(peerId, stream) {
    if (this.speakingDetectors.has(peerId)) {
      this.stopSpeakingDetection(peerId);
    }
    if (this.gainNodes.has(peerId)) {
      try { this.gainNodes.get(peerId).disconnect(); } catch (_) {}
      this.gainNodes.delete(peerId);
    }

    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;

    // Subtle volume normalization (boost slightly to compensate for quiet microphones)
    const gainNode = ctx.createGain();
    gainNode.gain.value = 1.15;

    const source = ctx.createMediaStreamSource(stream);
    source.connect(analyser);
    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    this.gainNodes.set(peerId, gainNode);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let running = false;
    let _animFrame = null;
    let _stillSpeaking = false;
    let _offTimer = null;
    let _lastSpokeAt = 0;
    let _history = [];

    const tick = () => {
      if (!running) return;
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      _history.push(avg);
      if (_history.length > 3) _history.shift();
      const smoothed = _history.reduce((a, b) => a + b, 0) / _history.length;
      const isCurrentlySpeaking = smoothed > 25;
      if (isCurrentlySpeaking) {
        _stillSpeaking = true;
        _lastSpokeAt = Date.now();
        if (this.onSpeakingChange) this.onSpeakingChange(peerId, true);
      } else if (_stillSpeaking) {
        if (Date.now() - _lastSpokeAt < 300) return;
        if (!_offTimer) {
          _offTimer = setTimeout(() => {
            _stillSpeaking = false;
            if (this.onSpeakingChange) this.onSpeakingChange(peerId, false);
            _offTimer = null;
          }, 300);
        }
      }
      _animFrame = requestAnimationFrame(tick);
    };

    const detector = {
      start() { running = true; tick(); },
      stop() {
        running = false;
        if (_animFrame) cancelAnimationFrame(_animFrame);
        clearTimeout(_offTimer);
        _history = [];
        ctx.close().catch(() => {});
      },
    };

    detector.start();
    this.speakingDetectors.set(peerId, detector);
  }

  /**
   * Stop speaking detection for a peer.
   * @param {number|string} peerId
   */
  stopSpeakingDetection(peerId) {
    const detector = this.speakingDetectors.get(peerId);
    if (detector) {
      detector.stop();
      this.speakingDetectors.delete(peerId);
    }
  }

  /**
   * Handle tab visibility changes — pause/resume speaking detection.
   * Called by ChatContext via document visibilitychange listener.
   */
  handleVisibilityChange() {
    if (document.hidden) {
      this.speakingDetectors.forEach(d => {
        d.running = false;
      });
    } else {
      this.speakingDetectors.forEach(d => {
        d.running = true;
        d._tick();
      });
    }
  }

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------

  _getMyId() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id || payload.sub;
    } catch {
      return null;
    }
  }

  /**
   * Get the remote stream for a peer.
   */
  getRemoteStream(peerId) {
    return this.remoteStreams.get(peerId) || null;
  }

  /**
   * Get all active peer IDs for a call.
   */
  getPeerIds(callId) {
    const peers = this.calls.get(callId);
    return peers ? Array.from(peers.keys()) : [];
  }
}

const webrtcService = new WebRTCService();
export default webrtcService;
